import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card, Col, Row, Typography, Image, Button, Select, Spin, Alert,
  Empty, Tag, Tooltip, Popconfirm, Space, Divider, DatePicker, Radio,
  message,
} from "antd";
import {
  DeleteOutlined, DownloadOutlined, LinkOutlined, ReloadOutlined,
  SyncOutlined, CalendarOutlined, SearchOutlined, CopyOutlined,
} from "@ant-design/icons";
import { BsCloudRainHeavy, BsCloudDrizzle, BsGlobe2, BsDropletHalf } from "react-icons/bs";
import { TbWorldLatitude, TbDropletOff } from "react-icons/tb";
import { WiDaySunny } from "react-icons/wi";
import dayjs from "dayjs";
import apiRb from "../../http/apiRb";

const { Title, Text } = Typography;

const API_BASE = process.env.REACT_APP_API_RB || '';

// ─── Mapeamento hierárquico de filtros ───────────────────────────────────────
// secoes: array de IDs da tabela clima_api (1-8)
// texto:  string para LIKE na cli_legenda (vazio = sem filtro de texto)
const ARVORE = [
  {
    pais: "Brasil",
    cor: "#1677ff",
    textoBase: "Brasil",
    icon: <BsCloudRainHeavy size={16} />,
    categorias: [
      {
        label: "Previsão",
        subs: [
          { label: "Chuva",    secoes: [1], texto: "Previsão Chuva - Brasil",          omitir: "Anomalia" },
          { label: "Anomalia", secoes: [1], texto: "Anomalia da Previsão Chuva - Brasil" },
        ],
      },
      {
        label: "Acumulado",
        subs: [
          { label: "Chuva",    secoes: [2], texto: "Chuva Acumulada - Brasil",          omitir: "Anomalia" },
          { label: "Anomalia", secoes: [2], texto: "Anomalia da Chuva Acumulada - Brasil" },
        ],
      },
      {
        label: "Água no Solo",
        subs: [
          { label: "Disponibilidade", secoes: [7], texto: "" },
        ],
      },
      {
        label: "Estiagem Agrícola",
        subs: [
          { label: "Estiagem", secoes: [8], texto: "" },
        ],
      },
    ],
  },
  {
    pais: "Estados Unidos",
    cor: "#d97706",
    textoBase: "Estados Unidos",
    icon: <TbWorldLatitude size={16} />,
    categorias: [
      {
        label: "Previsão",
        subs: [
          { label: "Chuva",    secoes: [5], texto: "Previsão Chuva - Estados Unidos",          omitir: "Anomalia" },
          { label: "Anomalia", secoes: [5], texto: "Anomalia da Previsão Chuva - Estados Unidos" },
        ],
      },
      {
        label: "Acumulado",
        subs: [
          { label: "Chuva",    secoes: [6], texto: "Chuva Acumulada - Estados Unidos", omitir: "Anomalia" },
          { label: "Anomalia", secoes: [6], texto: "Anomalia da Chuva Acumulada - Estados Unidos" },
        ],
      },
      {
        label: "Monitor de Seca",
        subs: [
          { label: "EUA (geral)",   secoes: [6], texto: "Monitor de Seca" },
          { label: "Por Estado",    secoes: [6], texto: "Seca - Estado" },
          { label: "Por Região",    secoes: [6], texto: "Seca - Região" },
        ],
      },
    ],
  },
  {
    pais: "Argentina",
    cor: "#7c3aed",
    textoBase: "Argentina",
    icon: <BsCloudDrizzle size={16} />,
    categorias: [
      {
        label: "Previsão",
        subs: [
          { label: "Chuva",    secoes: [3], texto: "Previsão Chuva - Argentina",          omitir: "Anomalia" },
          { label: "Anomalia", secoes: [3], texto: "Anomalia da Previsão Chuva - Argentina" },
        ],
      },
      {
        label: "Acumulado",
        subs: [
          { label: "Chuva",    secoes: [4], texto: "Chuva Acumulada - Argentina",          omitir: "Anomalia" },
          { label: "Anomalia", secoes: [4], texto: "Anomalia da Chuva Acumulada - Argentina" },
        ],
      },
    ],
  },
  {
    pais: "América do Sul",
    cor: "#059669",
    textoBase: "América do Sul",
    icon: <BsGlobe2 size={16} />,
    categorias: [
      {
        label: "Previsão",
        subs: [
          { label: "Chuva",    secoes: [5], texto: "Previsão Chuva - América do Sul",          omitir: "Anomalia" },
          { label: "Anomalia", secoes: [5], texto: "Anomalia da Previsão Chuva - América do Sul" },
        ],
      },
      {
        label: "Acumulado",
        subs: [
          { label: "Chuva",    secoes: [5], texto: "Chuva Acumulada - América do Sul",          omitir: "Anomalia" },
          { label: "Anomalia", secoes: [5], texto: "Anomalia da Chuva Acumulada - América do Sul" },
        ],
      },
    ],
  },
];

// Cores por seção (mantidas para o card border)
const COR_SECAO = {
  1: "#1677ff", 2: "#0ea5e9", 3: "#7c3aed", 4: "#6d28d9",
  5: "#059669", 6: "#d97706", 7: "#0891b2", 8: "#dc2626",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function imgUrl(filename) { return `${API_BASE}/rb_clima/${filename}`; }

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// Copia o mapa como <img> pronto pra colar no corpo da notícia/análise —
// usa o mesmo mecanismo de captura via clipboard já usado pelos gráficos
// de Comex/USDA/IPAR (ver openReportPopupAndCapture em functions/utils.js).
// Diferente da geração sob demanda em Clima.js (que só devolve base64
// efêmero), aqui a imagem já está salva com URL real no servidor — por
// isso funciona de forma confiável, igual ao banco de imagens.
function copiarParaEditor(filename, legenda) {
  const html = `<p><img src="${imgUrl(filename)}" alt="${legenda}" style="max-width:100%;" /></p>`;
  navigator.clipboard
    .writeText(html)
    .then(() => message.success("Mapa copiado! Volte ao editor pra inserir."))
    .catch(() => message.error("Não foi possível copiar o mapa."));
}

function baixar(filename, nome) {
  const a = document.createElement('a');
  a.href = imgUrl(filename);
  a.download = `${nome.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.png`;
  a.target = '_blank';
  a.click();
}

// ─── Componente ──────────────────────────────────────────────────────────────
export default function ClimaMapas() {
  // Seleção hierárquica
  const [paisSel,      setPaisSel]      = useState(null);
  const [categoriaSel, setCategoriaSel] = useState(null);
  const [subSel,       setSubSel]       = useState(null);

  // Filtro de data
  // Aberto pelo atalho dos editores (?popup=1), começa em "hoje" em vez de
  // "todos". Com "todos" e sem local escolhido a busca é curto-circuitada de
  // propósito — para não carregar o acervo inteiro — e a janela abria vazia,
  // o que fazia o atalho parecer quebrado: nenhum mapa, nenhum botão de
  // copiar. Na página avulsa o padrão continua "todos".
  const [modoData, setModoData] = useState(() =>
    new URLSearchParams(window.location.search).get("popup") === "1" ? "hoje" : "todos"
  ); // "todos" | "hoje" | "data"
  const [dataSel,  setDataSel]  = useState(null);    // dayjs object

  // Resultado
  const [mapas,       setMapas]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [erro,        setErro]        = useState(null);
  const [regenerando, setRegenerando] = useState({});

  // ── Listas derivadas ──────────────────────────────────────────────────────
  const paisNode      = useMemo(() => ARVORE.find(p => p.pais === paisSel), [paisSel]);
  const categoriaNode = useMemo(() => paisNode?.categorias.find(c => c.label === categoriaSel), [paisNode, categoriaSel]);
  const subNode       = useMemo(() => categoriaNode?.subs.find(s => s.label === subSel), [categoriaNode, subSel]);

  const categoriasOpts = paisNode?.categorias.map(c => c.label) ?? [];
  const subsOpts       = categoriaNode?.subs.map(s => s.label) ?? [];

  // ── Parâmetros de busca resolvidos ───────────────────────────────────────
  const parametros = useMemo(() => {
    const secoes = subNode?.secoes
      ?? (categoriaNode ? categoriaNode.subs.flatMap(s => s.secoes)
        : (paisNode ? paisNode.categorias.flatMap(c => c.subs.flatMap(s => s.secoes)) : []));
    // Quando só o país está selecionado (sem categoria/subtipo), usa textoBase para
    // evitar que seção 5 (EUA + América do Sul) retorne resultados misturados.
    const texto = subNode?.texto
      ?? (categoriaNode ? "" : (paisNode?.textoBase ?? ""));
    const omitir = subNode?.omitir ?? null;
    const data = modoData === "hoje"  ? dayjs().format("YYYY-MM-DD")
               : modoData === "data" && dataSel ? dataSel.format("YYYY-MM-DD")
               : null;
    return { secoes: [...new Set(secoes)], texto, omitir, data };
  }, [subNode, categoriaNode, paisNode, modoData, dataSel]);

  // ── Busca ─────────────────────────────────────────────────────────────────
  const buscar = useCallback(async (params) => {
    if (!params.secoes.length && !params.texto && !params.data) {
      setMapas([]);
      return;
    }
    setLoading(true);
    setErro(null);
    setMapas([]);
    try {
      const qs = new URLSearchParams();
      if (params.secoes.length) qs.set("secoes", params.secoes.join(","));
      if (params.texto)         qs.set("texto",  params.texto);
      if (params.omitir)        qs.set("omitir", params.omitir);
      if (params.data)          qs.set("data",   params.data);
      const { data } = await apiRb.get(`/clima/busca?${qs}`);
      setMapas(Array.isArray(data) ? data : []);
    } catch (e) {
      setErro(e?.response?.data?.error || e?.message || "Erro ao buscar mapas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    buscar(parametros);
  }, [parametros, buscar]);

  // ── Ações ─────────────────────────────────────────────────────────────────
  const excluir = useCallback(async (id) => {
    try {
      await apiRb.delete(`/clima/db/${id}`);
      setMapas(prev => prev.filter(m => m.cli_id !== id));
    } catch (e) {
      setErro(e?.response?.data?.error || e?.message || "Erro ao excluir");
    }
  }, []);

  const regenerar = useCallback(async (mapa) => {
    setRegenerando(prev => ({ ...prev, [mapa.cli_id]: true }));
    setErro(null);
    try {
      await apiRb.get(`/clima/regenerar-db/${mapa.cli_id}`, { timeout: 120000 });
      buscar(parametros);
    } catch (e) {
      setErro(e?.response?.data?.error || e?.message || "Erro ao regenerar mapa");
    } finally {
      setRegenerando(prev => ({ ...prev, [mapa.cli_id]: false }));
    }
  }, [buscar, parametros]);

  // ── Render ────────────────────────────────────────────────────────────────
  const semFiltro = !paisSel && modoData === "todos";

  return (
    <div style={{ padding: 24 }}>
      {/* Cabeçalho */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <WiDaySunny size={36} color="#f59e0b" />
        <div>
          <Title level={3} style={{ margin: 0 }}>Histórico de Mapas Climáticos</Title>
          <Text type="secondary">Mapas gerados e processados com GPT-4o + Sharp</Text>
        </div>
      </div>

      <Divider style={{ marginTop: 12, marginBottom: 20 }} />

      {/* ── Painel de filtros ───────────────────────────────────────────── */}
      <Card
        size="small"
        style={{ marginBottom: 20, borderRadius: 10 }}
        styles={{ body: { padding: "16px 20px" } }}
      >
        {/* Linha 1 — Data */}
        <Space wrap style={{ marginBottom: 14 }}>
          <CalendarOutlined style={{ color: "#6b7280" }} />
          <Text strong style={{ minWidth: 40 }}>Data:</Text>
          <Radio.Group
            value={modoData}
            onChange={e => { setModoData(e.target.value); setDataSel(null); }}
            size="small"
          >
            <Radio.Button value="todos">Todos</Radio.Button>
            <Radio.Button value="hoje">Hoje</Radio.Button>
            <Radio.Button value="data">Data específica</Radio.Button>
          </Radio.Group>
          {modoData === "data" && (
            <DatePicker
              value={dataSel}
              onChange={setDataSel}
              format="DD/MM/YYYY"
              size="small"
              allowClear
              placeholder="Dia / Mês / Ano"
            />
          )}
        </Space>

        <Divider style={{ margin: "8px 0 14px" }} />

        {/* Linha 2 — Hierarquia de localização */}
        <Space wrap align="center">
          <SearchOutlined style={{ color: "#6b7280" }} />
          <Text strong style={{ minWidth: 40 }}>Local:</Text>

          {/* País / Região */}
          <Select
            placeholder="País / Região"
            value={paisSel}
            onChange={v => { setPaisSel(v); setCategoriaSel(null); setSubSel(null); }}
            allowClear
            style={{ minWidth: 170 }}
            size="small"
          >
            {ARVORE.map(p => (
              <Select.Option key={p.pais} value={p.pais}>
                <Space size={4}>
                  <span style={{ color: p.cor }}>{p.icon}</span>
                  {p.pais}
                </Space>
              </Select.Option>
            ))}
          </Select>

          {/* Categoria */}
          {categoriasOpts.length > 0 && (
            <Select
              placeholder="Categoria"
              value={categoriaSel}
              onChange={v => { setCategoriaSel(v); setSubSel(null); }}
              allowClear
              style={{ minWidth: 160 }}
              size="small"
            >
              {categoriasOpts.map(c => (
                <Select.Option key={c} value={c}>{c}</Select.Option>
              ))}
            </Select>
          )}

          {/* Subtipo */}
          {subsOpts.length > 0 && (
            <Select
              placeholder="Tipo"
              value={subSel}
              onChange={setSubSel}
              allowClear
              style={{ minWidth: 160 }}
              size="small"
            >
              {subsOpts.map(s => (
                <Select.Option key={s} value={s}>{s}</Select.Option>
              ))}
            </Select>
          )}

          <Tooltip title="Recarregar">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => buscar(parametros)}
              loading={loading}
            />
          </Tooltip>
        </Space>

        {/* Tags de resumo do filtro ativo */}
        {!semFiltro && (
          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Filtros ativos:</Text>
            {modoData === "hoje" && <Tag color="blue">Hoje</Tag>}
            {modoData === "data" && dataSel && <Tag color="blue">{dataSel.format("DD/MM/YYYY")}</Tag>}
            {paisSel      && <Tag color={paisNode?.cor ?? "default"}>{paisSel}</Tag>}
            {categoriaSel && <Tag>{categoriaSel}</Tag>}
            {subSel       && <Tag>{subSel}</Tag>}
            {!loading && <Tag style={{ marginLeft: 4 }}>{mapas.length} resultado{mapas.length !== 1 ? "s" : ""}</Tag>}
          </div>
        )}
      </Card>

      {/* Estados */}
      {semFiltro && !loading && (
        <Empty
          description="Selecione um filtro de data ou local para visualizar os mapas."
          style={{ padding: 60 }}
        />
      )}

      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Spin size="large" tip="Buscando mapas..." />
        </div>
      )}

      {!loading && erro && (
        <Alert type="error" showIcon message="Erro ao buscar" description={erro} style={{ marginBottom: 16 }} />
      )}

      {!loading && !erro && !semFiltro && mapas.length === 0 && (
        <Empty description="Nenhum mapa encontrado para os filtros selecionados." style={{ padding: 60 }} />
      )}

      {/* Grid */}
      {!loading && mapas.length > 0 && (
        <Image.PreviewGroup>
          <Row gutter={[16, 16]}>
            {mapas.map((mapa) => {
              const cor = COR_SECAO[mapa.cli_secao] ?? "#6b7280";
              return (
                <Col key={mapa.cli_id} xs={24} sm={12} md={12} lg={8} xl={6}>
                  <Card
                    style={{ borderTop: `3px solid ${cor}`, borderRadius: 10, height: "100%" }}
                    styles={{ body: { padding: 12 } }}
                    actions={[
                      <Tooltip title="Copiar para o editor" key="copy">
                        <Button
                          type="text"
                          icon={<CopyOutlined />}
                          onClick={() => copiarParaEditor(mapa.cli_imagem, mapa.cli_legenda)}
                        />
                      </Tooltip>,
                      <Tooltip title="Ver imagem original" key="orig">
                        <Button
                          type="text"
                          icon={<LinkOutlined />}
                          onClick={() => window.open(`${API_BASE}/clima/original-proxy/${mapa.cli_id}`, '_blank')}
                        />
                      </Tooltip>,
                      <Tooltip title="Baixar imagem" key="dl">
                        <Button
                          type="text"
                          icon={<DownloadOutlined />}
                          onClick={() => baixar(mapa.cli_imagem, mapa.cli_legenda)}
                        />
                      </Tooltip>,
                      <Tooltip title="Regenerar" key="reg">
                        <Button
                          type="text"
                          icon={<SyncOutlined spin={!!regenerando[mapa.cli_id]} />}
                          loading={!!regenerando[mapa.cli_id]}
                          onClick={() => regenerar(mapa)}
                        />
                      </Tooltip>,
                      <Popconfirm
                        key="del"
                        title="Excluir este mapa?"
                        description="Esta ação não pode ser desfeita."
                        onConfirm={() => excluir(mapa.cli_id)}
                        okText="Excluir"
                        cancelText="Cancelar"
                        okButtonProps={{ danger: true }}
                      >
                        <Tooltip title="Excluir">
                          <Button type="text" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                      </Popconfirm>,
                    ]}
                  >
                    <div style={{ marginBottom: 8, borderRadius: 6, overflow: "hidden", background: "#f3f4f6" }}>
                      <Image
                        src={imgUrl(mapa.cli_imagem)}
                        alt={mapa.cli_legenda}
                        style={{ width: "100%", height: "auto", objectFit: "contain", maxHeight: 280, display: "block" }}
                        preview={{ mask: "Ampliar" }}
                      />
                    </div>

                    <Tooltip title={mapa.cli_legenda}>
                      <Text
                        strong
                        style={{ fontSize: 12, color: cor, display: "block", marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {mapa.cli_legenda}
                      </Text>
                    </Tooltip>

                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {formatDate(mapa.cli_data)}
                    </Text>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Image.PreviewGroup>
      )}
    </div>
  );
}
